import { MonthlyInstallmentsOver12Months, ProposalStep3AllAttributesDto, ProposalStep3AllExtraAttributesDto, ProposalStep3ServiceAnnual, ProposalStep3ServiceMonthly, ProposalStep3ServiceOneTime, ProposalStep3ServiceSession, ProposalStep3ServiceUnit, ProposalStep3SingleDto, step3Service } from "@gap/backend/app-services/proposal/models/ProposalStep3Dto"
import { PaymentMethodOptionValue, ServiceRateTypeEnum } from "@gap/backend/app-services/services/models/ServiceRateTypesDto"
import { z } from "zod"
import { annualPaymentDefaults, paymentOnCompletionDefaults, stagedPaymentsDefaults, upfrontPaymentDefaults, monthlyInstallmentsOver12MonthsDefaults, defaultServicePrice, defaultRateType, defaultServiceUnits } from "./proposalsConstants"
import { ServiceWithLinkedProcessDto } from "@gap/backend/app-services/services/models/ServiceDto"
import { PartialRecord, PickByValue, formatCurrency } from "@gap/utils/utils"
import { ProposalDto } from "@gap/backend/app-services/proposal/models/ProposalDto"
import { endOfDay } from 'date-fns'

type JustTheExtras<T> = Omit<T, 'services' | 'serviceRateType' | 'monthlyInstallmentsOver12Months'>
type DefaultExtraProperties<T> = { serviceRateType: T } & z.output<typeof MonthlyInstallmentsOver12Months>
type ObjectType<T> =
   T extends "Per month" ? JustTheExtras<z.output<typeof ProposalStep3ServiceMonthly>> & DefaultExtraProperties<T> :
   T extends "Per annum" ? JustTheExtras<z.output<typeof ProposalStep3ServiceAnnual>> & DefaultExtraProperties<T> :
   T extends "One-time fee" ? JustTheExtras<z.output<typeof ProposalStep3ServiceOneTime>> & DefaultExtraProperties<T> :
   T extends "Per session" ? JustTheExtras<z.output<typeof ProposalStep3ServiceSession>> & DefaultExtraProperties<T> :
   T extends "Per unit" ? JustTheExtras<z.output<typeof ProposalStep3ServiceUnit>> & DefaultExtraProperties<T> :
   never

export type ProposalStatus = 'draft' | 'overdue' | 'sent' | 'accepted' | 'archived'

export const getDefaultValuesPerRateType = <T extends ServiceRateTypeEnum>(serviceRateType: T): ObjectType<T> => {
   switch (serviceRateType) {
      case 'Per month':
         return {
            serviceRateType,
            ...monthlyInstallmentsOver12MonthsDefaults,
            ...annualPaymentDefaults
         } as ObjectType<T>
      case 'Per annum':
         return {
            serviceRateType,
            ...monthlyInstallmentsOver12MonthsDefaults,
            ...annualPaymentDefaults,
            ...paymentOnCompletionDefaults,
            ...stagedPaymentsDefaults
         } as ObjectType<T>
      case 'One-time fee':
         return {
            serviceRateType,
            ...monthlyInstallmentsOver12MonthsDefaults,
            ...paymentOnCompletionDefaults,
            ...upfrontPaymentDefaults,
            ...stagedPaymentsDefaults
         } as ObjectType<T>
      case 'Per session':
         return {
            serviceRateType,
            ...monthlyInstallmentsOver12MonthsDefaults,
            ...upfrontPaymentDefaults
         } as ObjectType<T>
      case 'Per unit':
         return {
            serviceRateType,
            ...monthlyInstallmentsOver12MonthsDefaults,
            ...annualPaymentDefaults
         } as ObjectType<T>
      default:
         throw new Error(`Service rate type does not exists for ${serviceRateType}`)
   }
}

export const getServiceForStep = (service: ServiceWithLinkedProcessDto): z.output<typeof step3Service> => {
   const alsoIncludeServiceUnits = ['Per session', 'Per unit'].includes(service.pricing?.startingPriceRate || defaultRateType)
   return {
      serviceId: service.id,
      servicePrice: service.pricing?.startingPrice || defaultServicePrice,
      ...(alsoIncludeServiceUnits ? { serviceUnits: defaultServiceUnits } : {}),
      serviceAgreementType: 'agreed'
   };
}

export type PaymentBlockType = 'Monthly instalments over 12-month period' | 'Annual payment' | 'Payment on completion' | 'Staged payments' | 'Upfront payment in full'

export const canSeeBasedOnBlockType: Record<PaymentBlockType, ServiceRateTypeEnum[]> = {
   "Monthly instalments over 12-month period": ['One-time fee', 'Per annum', 'Per month', 'Per session', 'Per unit'],
   "Annual payment": ['Per month', 'Per annum', 'Per unit'],
   "Payment on completion": ['Per annum', 'One-time fee'],
   "Staged payments": ['Per annum', 'One-time fee'],
   "Upfront payment in full": ['One-time fee', 'Per session']
}

export const checkFieldNameForBlockType: Record<PaymentBlockType, keyof PickByValue<ProposalStep3AllExtraAttributesDto, boolean>> = {
   "Monthly instalments over 12-month period": 'monthlyInstallmentsOver12Months',
   "Annual payment": 'annualPayment',
   "Payment on completion": 'paymentOnCompletion',
   "Staged payments": 'stagedPayments',
   "Upfront payment in full": 'upfrontPaymentInFull'
}

export const labelForFieldName: PartialRecord<keyof Partial<ProposalStep3AllExtraAttributesDto>, string> = {
   'annualPaymentDiscount': 'Discount',
   'stagedPaymentUpfront': 'Upfront',
   'stagedPaymentMilestone': 'Milestone',
   'upfrontPaymentInFullDiscount': 'Discount'
}

export function listPaymentOptionsForServiceRateType({ pricingItem: minimalItem }: { pricingItem: ProposalStep3SingleDto }) {
   const pricingItem = minimalItem as ProposalStep3AllAttributesDto
   const options: PaymentMethodOptionValue[] = []

   if (pricingItem.monthlyInstallmentsOver12Months) options.push('monthly-instalments')
   if (pricingItem.annualPayment) options.push('annual')
   if (pricingItem.upfrontPaymentInFull) options.push('upfront')
   if (pricingItem.stagedPayments) options.push('staged')

   return options
}

export function doesServiceRateTypeHaveEstimatedLineItems(pricingItem: ProposalStep3SingleDto) {
   return pricingItem
      .services.some(({ serviceAgreementType }) => serviceAgreementType === 'estimate')
}

export function getServiceRateTotalText(pricingItem: ProposalStep3SingleDto, paymentMethod: PaymentMethodOptionValue, currencyCode: string)
   : { totalText: string, estimateMarker: string | undefined, preDiscountText?: string } {

   const item = pricingItem as ProposalStep3AllAttributesDto
   const estimateMarker = doesServiceRateTypeHaveEstimatedLineItems(pricingItem) ? '*' : undefined
   const { services } = pricingItem
   const format = (value: number) => formatCurrency({ value, currencyCode })

   let runningTotal = services.reduce<number>((total, { servicePrice, serviceUnits }) => total
      + ((typeof serviceUnits === 'undefined' || !['Per session', 'Per unit'].includes(pricingItem.serviceRateType))
         ? servicePrice
         : servicePrice * serviceUnits), 0)

   if (item.serviceRateType === 'Per month') {
      runningTotal *= 12
   }

   if (item.monthlyInstallmentsOver12Months && paymentMethod === 'monthly-instalments') {
      const total = format(runningTotal / 12)

      return { totalText: `${total} / month`, estimateMarker }
   }

   if (item.stagedPayments && paymentMethod === 'staged') {
      const upfront = runningTotal * item.stagedPaymentUpfront / 100
      return {
         totalText: `${format(upfront)} upfront (${+item.stagedPaymentUpfront.toFixed(2)}%)`,
         estimateMarker,
      }
   }

   if (item.annualPayment && paymentMethod === 'annual') {
      const discounted = runningTotal - (item.annualPaymentDiscount * runningTotal / 100)
      return {
         totalText: `${format(discounted)} / year`,
         estimateMarker,
         ...(discounted === runningTotal ? {} : {
            preDiscountText: `${format(runningTotal)} / year`,
         })
      };
   }

   if (item.paymentOnCompletion && paymentMethod === 'on-completion') {
      return {
         totalText: `${format(runningTotal)} on completion`,
         estimateMarker,
      }
   }

   if (item.upfrontPaymentInFull && paymentMethod === 'upfront') {
      const discounted = runningTotal - (item.upfrontPaymentInFullDiscount * runningTotal / 100)
      return {
         totalText: `${format(discounted)} upfront`,
         estimateMarker,
         ...(discounted === runningTotal ? {} : {
            preDiscountText: `${format(runningTotal)} upfront`,
         })
      };
   }

   return { totalText: '', estimateMarker: undefined }
}

export const isProposalOverdue = (proposal: ProposalDto) => {
   // If due date is not set or proposal has not been sent out
   if (!proposal.step4?.dueDate || !proposal.step4.dueDateDate || !proposal.sentAt) {
      return false
   }
   const due = endOfDay(new Date(proposal.step4.dueDateDate))

   return new Date() > due
}

export const getProposalStatus = (proposal: ProposalDto): ProposalStatus => {
   if (proposal.archivedAt) {
      return 'archived'
   }

   if (proposal.acceptedAt) {
      return 'accepted'
   }

   if (isProposalOverdue(proposal)) {
      return 'overdue'
   }

   if (proposal.sentAt) {
      return 'sent'
   } else {
      return 'draft'
   }
}
